import React, { useEffect } from "react";
import { Route, Switch, useLocation } from "react-router-dom";
import { Container } from "semantic-ui-react";
import ModalManager from "../common/modals/ModalManager";
import Layout from "./Layout";
import {
  ACCOUNT_SIGN_IN_ROUTE,
  ADMIN_PATH,
  COMING_SOON_ROUTE,
  HANDLE_AUTH_PATH,
  UNDER_MAINTENANCE_ROUTE,
} from "../api/routeConstants";
import { toast, ToastContainer } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import LoadingComponent from "./LoadingComponent";
import PrivateRoute, { ADMIN_ROLE } from "./PrivateRoute";
import AdminDashboard from "../../features/admin/components/AdminDashboard";
import PageNotFound from "../common/errors/PageNotFound";
import AppEnvironmentMode from "./AppEnvironmentMode";
import ReactGA from "react-ga4";
import { getUserState } from "../../js/helpers/getSignedInUserType";
import CookieWrapper from "./CookieWrapper";
import { ErrorBoundary } from "react-error-boundary";
import ErrorOverlay from "../common/overlays/ErrorOverlay";
import { appError, loadAppMode } from "../store/appReducer";
import useFirestoreDoc from "../hooks/useFirestoreDoc";
import {
  FIRESTORE_APP_MODE_COLLECTION,
  FIRESTORE_APP_MODE_DOCUMENT_ID,
  QUERY_CODES,
} from "../firestore/firestoreConstants";
import AccountSignInPage from "../../features/auth/components/AccountSignInPage";
import { listenToDocFromFirestore } from "../firestore/firestoreService";
import AuthHandlerPage from "../../features/auth/components/AuthHandlerPage";
import { useIdle } from "react-use";
import { signOutUser } from "../../features/auth/authActions";
import { signOutFirebase } from "../firestore/firebaseService";
import { getJobs } from "../../features/jobs/jobsSelectors";

export default function App() {
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { initialised } = useSelector((state) => state.async);
  const { currentUserAccount } = useSelector((state) => state.account);
  const { appMode, isCookieConsent } = useSelector((state) => state.app);
  const { mode } = useSelector((state) => getJobs(state));

  useEffect(() => {
    // initialise GA once after initialised
    if (isCookieConsent && initialised) {
      ReactGA.initialize(process.env.REACT_APP_GA_TRACKING_ID, {
        gaOptions: {
          ...(currentUserAccount && { userId: currentUserAccount.id }),
        },
      });
    }
  }, [currentUserAccount, isCookieConsent, initialised]);

  useEffect(() => {
    if (isCookieConsent && initialised) {
      ReactGA.send("pageview");
      ReactGA.event({
        category: "Navigation",
        action: `${pathname}`,
        label: `${getUserState(currentUserAccount)} - ${mode}`,
      });
    }
  }, [currentUserAccount, pathname, isCookieConsent, initialised, mode]);

  const isIdle = useIdle(28800e3);

  useEffect(() => {
    if (isIdle && currentUserAccount) {
      async function signOut() {
        dispatch(signOutUser());
        await signOutFirebase();
      }
      signOut().then(() => {
        toast(
          <span className={"body-blue-text"}>
            You have been signed out due to account inactivity.
          </span>,
          {
            position: "top-right",
            autoClose: false,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          }
        );
      });
    }
  }, [currentUserAccount, dispatch, isIdle]);

  useFirestoreDoc({
    shouldExecute: initialised,
    query: () =>
      listenToDocFromFirestore({
        dbCollection: FIRESTORE_APP_MODE_COLLECTION,
        id: FIRESTORE_APP_MODE_DOCUMENT_ID,
      }),
    data: (data) => dispatch(loadAppMode({ ...data, isLoaded: true })),
    deps: [dispatch, initialised],
    queryCode: QUERY_CODES.getAppMode,
  });

  if (!initialised || !appMode.isLoaded)
    return <LoadingComponent initialLoading={true} />;

  if (pathname.includes(HANDLE_AUTH_PATH)) return <AuthHandlerPage />;

  function handleError(error, info) {
    dispatch(
      appError(
        "Error: " +
          error.toString() +
          " Stack: " +
          info.componentStack.toString()
      )
    );
  }

  const isOverlayPage =
    pathname === COMING_SOON_ROUTE || pathname === UNDER_MAINTENANCE_ROUTE;

  return (
    <ErrorBoundary FallbackComponent={ErrorOverlay} onError={handleError}>
      <CookieWrapper>
        <AppEnvironmentMode isOverlayPage={isOverlayPage}>
          <Layout isOverlayPage={isOverlayPage}>
            <ModalManager />
            <ToastContainer position="bottom-right" hideProgressBar />
            <Route
              path={"/(.*)"}
              render={() => (
                <Container className="main main-container" fluid>
                  <Switch>
                    <Route
                      exact
                      path={ACCOUNT_SIGN_IN_ROUTE}
                      render={(props) => <AccountSignInPage {...props} />}
                    />
                    <PrivateRoute
                      path={ADMIN_PATH}
                      component={AdminDashboard}
                      role={ADMIN_ROLE}
                    />
                    <Route path={""} component={PageNotFound} />
                  </Switch>
                </Container>
              )}
            />
          </Layout>
        </AppEnvironmentMode>
      </CookieWrapper>
    </ErrorBoundary>
  );
}
